/**
 * Route guards.
 *
 * The subtle requirement they implement is the **bootstrap window**: the access token lives in memory, so on a fresh
 * page load the app is momentarily unauthenticated even though the refresh cookie is valid. A guard that redirected
 * during that window would log the user out on every refresh, so `RequireAuth` renders a loader until
 * `isBootstrapped` is true and only then decides.
 *
 * The original address is preserved in `location.state.from`, so signing in returns the user to the page they asked
 * for instead of dropping them on the dashboard.
 */

import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { isAdministrator } from '@/components/layout/navItems';
import { Spinner } from '@/components/ui/surface';
import {
  POST_AUTH_ROUTE,
  SIGN_IN_ROUTE,
  useCurrentUser,
  useIsAuthenticated,
} from '@/features/auth/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';

/** The screen that finishes verification (TASK-0062). */
export const VERIFY_ROUTE = '/verify';

/** Full-screen placeholder shown while the silent session restore is running. */
function BootstrapPlaceholder() {
  const { t } = useTranslation();

  return (
    <div className="grid min-h-screen place-items-center bg-background" aria-busy="true">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Spinner className="h-6 w-6" />
        <p className="text-sm">{t('common.loading')}</p>
      </div>
    </div>
  );
}

/** Allows the subtree only for a signed-in account; otherwise sends the visitor to the sign-in screen. */
export function RequireAuth() {
  const isBootstrapped = useAuthStore((state) => state.isBootstrapped);
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();

  if (!isBootstrapped) {
    return <BootstrapPlaceholder />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={SIGN_IN_ROUTE}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return <Outlet />;
}

/** Allows the subtree only for a visitor without a session; a signed-in user is sent to the application. */
export function RequireGuest() {
  const isBootstrapped = useAuthStore((state) => state.isBootstrapped);
  const isAuthenticated = useIsAuthenticated();

  if (!isBootstrapped) {
    return <BootstrapPlaceholder />;
  }

  if (isAuthenticated) {
    return <Navigate to={POST_AUTH_ROUTE} replace />;
  }

  return <Outlet />;
}

/**
 * Allows the subtree only for an account that has proved the identifier the policy requires; otherwise sends it to `/verify`.
 *
 * Nested **inside** `RequireAuth`, so it only ever judges a signed-in account. The address being left is carried in `returnTo`, which
 * the verification screen honours once the identifier is verified. This guard is a courtesy, never the security boundary: a client
 * that skipped it would still be refused by the API.
 *
 * The decision reads the profile's `verification` object and deliberately **not** `emailConfirmed`: an account can be verified on the
 * mobile channel with an unconfirmed address, and the reverse, so a boolean would both refuse and admit incorrectly.
 *
 * Administrators are exempt, and the API answers the same way: `IdentityVerificationPolicy.Evaluate` returns a satisfied verdict for an
 * account holding the `Admin` role, so the profile this guard reads already says `isVerified`. The exemption is not a courtesy of the
 * client — an administrator left unverified by a policy change can still reach the product, and the administration surface stays
 * reachable for the person who has to repair it.
 */
export function RequireVerifiedIdentity() {
  const user = useCurrentUser();
  const location = useLocation();

  if (!isAdministrator(user) && user?.verification && !user.verification.isVerified) {
    const returnTo = `${location.pathname}${location.search}`;

    return <Navigate to={`${VERIFY_ROUTE}?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return <Outlet />;
}

/**
 * Allows the subtree only for administrators.
 *
 * The answer is a redirect to the 403 screen rather than the 404 screen: hiding the existence of the area would make a genuine
 * misconfiguration indistinguishable from a missing route while debugging.
 */
export function RequireAdministrator() {
  const user = useCurrentUser();

  if (!isAdministrator(user)) {
    return <Navigate to="/forbidden" replace />;
  }

  return <Outlet />;
}
